import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {IsGranted} from '../../../../decorator/is-granted';
import {MetaTitle} from '../../../../decorator/meta-title';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Paginator} from '../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../tools/table-sorter/table-sorter';
import {LoaderService} from '../../../../services/loader/loader.service';
import {LeadService} from '../../../../services/lead/lead.service';
import {Lead} from '../../../../models/lead';
import {JsonPipe, NgOptimizedImage} from '@angular/common';
import {ParisDatePipe} from '../../../../pipes/date/paris-date.pipe';
import {PaginatorComponent} from '../../../layout/paginator/paginator.component';
import {TableSorterHeaderDirective} from '../../../../directives/table-sorter/table-sorter-header.directive';
import {TableSorterDirective} from '../../../../directives/table-sorter/table-sorter.directive';
import {CountryService} from '../../../../services/country/country.service';
import {LeadDetailComponent} from './lead-detail/lead-detail.component';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {Partner} from '../../../../models/partner/partner';
import {QueryFilterService} from '../../../../services/query-filter/query-filter.service';
import {ClickDetailComponent} from '../click-log/click-detail/click-detail.component';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import {DisplayTableColumnComponent} from '../../../layout/display-table-column/display-table-column.component';
import {LogFiltersComponent} from '../../../layout/log-filters/log-filters.component';
import {LogForm} from '../../../../forms/log-form';
import {LogFilter} from '../../../../types/log-filter';
import {getTableHeaders} from '../../../../helpers/table.helper';
import {MoneyPipe} from '../../../../pipes/money/money.pipe';
import {IsGrantedDirective} from '../../../../directives/is-granted/is-granted.directive';
import {CsvExportService} from '../../../../services/csv-export/csv-export.service';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {CsvExportProgressComponent} from '../../../layout/csv-export-progress/csv-export-progress.component';
import {TooltipDirective} from '../../../../directives/tooltip/tooltip.directive';

@Component({
  selector: 'app-lead-log',
  imports: [
    ReactiveFormsModule,
    ParisDatePipe,
    PaginatorComponent,
    TableSorterHeaderDirective,
    TableSorterDirective,
    FormsModule,
    NgOptimizedImage,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    DisplayTableColumnComponent,
    LogFiltersComponent,
    MoneyPipe,
    IsGrantedDirective,
    CsvExportProgressComponent,
    TooltipDirective,
    JsonPipe,
  ],
  templateUrl: './lead-log.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './lead-log.component.scss'
})
@IsGranted(['LEAD_VIEW'])
@MetaTitle('Lead log')
export class LeadLogComponent implements OnInit {
  partners: Partner[] = [];
  leads: Lead[] = [];
  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('createdAt', 'desc');

  displayedColumns = {
    cid: true,
    product: true,
    offer: true,
    landingPage: false,
    partner: true,
    subid: false,
    tc1: false,
    tc2: false,
    tc3: false,
    tc4: false,
    clickid: false,
    ip: false,
    firstname: false,
    language: false,
    country: true,
    cost: false,
    sourceType: false,
    sourceDetails: false
  }

  filters: LogFilter = LogForm.initFilters({
    cid: '',
    sourceTypology: [],
    status: null,
    paid: null,
    fingerprint: null,
    email: null
  });

  downloading: boolean = false;
  exportProgress: { current: number; total: number } | null = null;

  constructor(
    protected leadService: LeadService,
    protected countryService: CountryService,
    private loader: LoaderService,
    private dialog: DialogService,
    private queryFilterService: QueryFilterService,
    private csvExportService: CsvExportService,
    private snackbar: SnackbarService,
  ) {
  }

  ngOnInit() {
    this.queryFilterService.init(this.paginator, this.tableSorter);

    this.filters = this.queryFilterService.initFilters();
    this.filters['cid'] = this.queryFilterService.getFilter('cid');
    this.filters['sourceTypology'] = this.queryFilterService.getFilter('sourceTypology')?.split(',') ?? [];
    this.filters['status'] = this.queryFilterService.getFilter('status') ?? null;
    this.filters['paid'] = this.queryFilterService.getBooleanFilter('paid');
    this.filters['fingerprint'] = this.queryFilterService.getBooleanFilter('fingerprint');
    this.filters['email'] = this.queryFilterService.getFilter('email');

    this.loader.startLoading();
  }

  applyFilters(): void {
    this.paginator.currentPage = 1;
    this.fetchAll();
  }

  fetchAll(): void {
    this.loader.startLoading();

    let params = this.getParameters();

    this.queryFilterService.setFilters(params);

    this.leadService.findAll(params, this.paginator, this.tableSorter).subscribe(leads => {
      this.leads = leads;
      this.loader.stopLoading();
    });
  }

  openDetail(lead: Lead): void {
    if (lead.id) {
      this.dialog.openDialog(LeadDetailComponent, {
        class: 'modal-lg',
        data: {
          id: lead.id,
        }
      }).subscribe(() => {
      });
    }
  }

  openClickDetail(lead: Lead): void {
    if (lead.cid) {
      this.dialog.openDialog(ClickDetailComponent, {
        class: 'modal-lg',
        data: {
          id: lead.cid
        }
      }).subscribe(() => {
      });
    }
  }

  downloadCsv(): void {
    if (this.downloading) {
      return;
    }

    const params = this.getParameters();
    params['headers'] = getTableHeaders('leads');

    if (!params.startDate || !params.endDate) {
      this.snackbar.error('Please select a date range before exporting.');
      return;
    }

    this.downloading = true;
    this.exportProgress = null;

    this.csvExportService.export({
      endpoint: '/lead/download',
      params,
      filename: 'lead-logs.csv'
    }).subscribe({
      next: (event) => {
        if (event.type === 'progress') {
          this.exportProgress = { current: event.current, total: event.total };
        } else {
          this.downloading = false;
          this.exportProgress = null;
          if (event.truncatedChunks > 0) {
            this.snackbar.warning(
              `${event.truncatedChunks} of ${event.chunkCount} chunk(s) were truncated. The export may be incomplete — try a smaller date range.`
            );
          }
        }
      },
      error: () => {
        this.downloading = false;
        this.exportProgress = null;
        this.snackbar.error('CSV export failed. Please try again.');
      }
    });
  }

  private getParameters(): any {
    let params = LogForm.getFilters(this.filters, 'product,offer,landingPage,partner');

    if (this.filters['cid']) {
      params.cid = this.filters['cid'];
    }

    if (this.filters['sourceTypology'] && this.filters['sourceTypology'].length) {
      params.sourceTypology = this.filters['sourceTypology'].join(',');
    }

    if (this.filters['status'] !== null) {
      params.status = this.filters['status'];
    }

    if (this.filters['paid'] !== null) {
      params.paid = this.filters['paid'] ? 1 : 0;
    }

    if (this.filters['fingerprint'] !== null) {
      params.fingerprint = this.filters['fingerprint'] ? 1 : 0;
    }

    if (this.filters['email']) {
      params.email = this.filters['email'];
    }

    return params;
  }
}
